import { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react'
import { TickerSheet } from '@/v2/v3/TickerSheet'
import { useAtlasStocks } from '@/v2/hooks/useAtlasStocks'
import { useLivePrices } from '@/v2/hooks/useLivePrices'

const Ctx = createContext(null)

// Reads `?stock=TICKER` from current URL on mount + listens for browser
// back/forward. Pushes URL state when user opens/closes the drawer so the
// detail view is bookmarkable + shareable.
function readUrlTicker() {
  if (typeof window === 'undefined') return null
  const t = new URLSearchParams(window.location.search).get('stock')
  return t ? t.toUpperCase() : null
}

function pushUrlTicker(ticker) {
  if (typeof window === 'undefined') return
  const url = new URL(window.location.href)
  if (ticker) url.searchParams.set('stock', ticker)
  else url.searchParams.delete('stock')
  window.history.pushState({}, '', url.toString())
}

// `extraStocks` lets callers (e.g. Discover) supply additional ticker rows
// not present in /api/atlas-data — that endpoint filters by active=true so
// hunting-tier stocks are excluded. Without this prop, ticker clicks on
// hunting tickers silently no-op because the lookup returns null.
export function StockDetailProvider({ children, extraStocks = [] }) {
  const { stocks } = useAtlasStocks()
  const [ticker, setTicker] = useState(() => readUrlTicker())

  // Always fetch live prices for the open ticker so the drawer has data even
  // when the click came from outside the visible price-batch.
  const focusTickers = useMemo(() => ticker ? [ticker] : [], [ticker])
  const { prices: focusPrices } = useLivePrices(focusTickers)
  // Historical fetch for 5D/1M/6M/1Y. Fetch once on open (refreshMs=0),
  // re-fetches when the ticker changes. Snapshot wins on shared fields.
  const { prices: histPrices } = useLivePrices(focusTickers, 0, 'historical')
  const mergedFocusPrices = useMemo(() => {
    if (!ticker) return focusPrices
    return { ...focusPrices, [ticker]: { ...histPrices[ticker], ...focusPrices[ticker] } }
  }, [focusPrices, histPrices, ticker])

  // Merge atlas-data stocks with caller-supplied extras (dedup by ticker;
  // atlas-data wins on conflict since it has the richer shape).
  const mergedStocks = useMemo(() => {
    if (!extraStocks || extraStocks.length === 0) return stocks
    const seen = new Set(stocks.map((s) => s.ticker))
    const extras = extraStocks.filter((s) => s?.ticker && !seen.has(s.ticker))
    return extras.length ? [...stocks, ...extras] : stocks
  }, [stocks, extraStocks])

  const stock = useMemo(
    () => ticker ? mergedStocks.find((s) => s.ticker === ticker) ?? null : null,
    [ticker, mergedStocks],
  )

  const openTicker = useCallback((t) => {
    if (!t) return
    const up = String(t).toUpperCase()
    // Guard the stuck-URL bug: if the ticker isn't in our universe
    // (atlas-data + any caller-supplied extras), the drawer at line 84
    // won't render. Without this check, openTicker would still push
    // ?stock=XYZ to the URL and leave the user looking at the same
    // page with no visible drawer and no obvious way back (only ESC
    // worked, which isn't discoverable). No-op + warn instead.
    if (!mergedStocks.some((s) => s.ticker === up)) {
      console.warn('[StockDetailProvider] openTicker(' + up + ') skipped — ticker not in universe')
      return
    }
    setTicker(up)
    pushUrlTicker(up)
  }, [mergedStocks])

  const closeDetail = useCallback(() => {
    setTicker(null)
    pushUrlTicker(null)
  }, [])

  // Membership probe for callers that want VISIBLE feedback instead of the
  // openTicker no-op (e.g. Agent Desk rows — agents can hold off-universe
  // tickers). true = will open; false = not in universe; null = universe not
  // loaded yet (unknown — treat as a silent no-op, don't flash "not covered").
  const hasTicker = useCallback((t) => {
    if (!t) return false
    if (mergedStocks.length === 0) return null
    const up = String(t).toUpperCase()
    return mergedStocks.some((s) => s.ticker === up)
  }, [mergedStocks])

  // ESC closes
  useEffect(() => {
    if (!ticker) return
    const handler = (e) => { if (e.key === 'Escape') closeDetail() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [ticker, closeDetail])

  // Back/forward syncs
  useEffect(() => {
    const handler = () => setTicker(readUrlTicker())
    window.addEventListener('popstate', handler)
    return () => window.removeEventListener('popstate', handler)
  }, [])

  // Clean up a stale ?stock=XYZ URL parameter that survived from a
  // previous session or a shared link, when XYZ isn't in our universe.
  // Without this, the URL stays set forever (no drawer, no fix from
  // the UI) and any future openTicker for a *valid* ticker has to fight
  // back/forward history that's already wrong. Wait for mergedStocks
  // to actually load before clearing so we don't false-clear during
  // the initial fetch.
  useEffect(() => {
    if (!ticker) return
    if (mergedStocks.length === 0) return
    if (mergedStocks.some((s) => s.ticker === ticker)) return
    console.warn('[StockDetailProvider] clearing stale ?stock=' + ticker + ' — not in universe')
    setTicker(null)
    pushUrlTicker(null)
  }, [ticker, mergedStocks])

  const value = useMemo(() => ({ openTicker, closeDetail, hasTicker, openTickerSymbol: ticker }), [openTicker, closeDetail, hasTicker, ticker])

  return (
    <Ctx.Provider value={value}>
      {children}
      {stock && (
        // .v3-root so the sheet inherits the true-black canvas + v3 fonts even
        // though the provider mounts high in the tree (possibly outside one).
        <div className="v3-root">
          <TickerSheet
            stock={stock}
            livePrices={mergedFocusPrices}
            allStocks={mergedStocks}
            onClose={closeDetail}
            onTickerClick={openTicker}
          />
        </div>
      )}
    </Ctx.Provider>
  )
}

export function useStockDetail() {
  const ctx = useContext(Ctx)
  if (!ctx) {
    // Safety: pages rendered outside the provider should fail soft, not crash
    return { openTicker: () => {}, closeDetail: () => {}, hasTicker: () => null, openTickerSymbol: null }
  }
  return ctx
}
